import {DefaultValues, FieldValues, Path, SubmitHandler, useForm, UseFormReturn} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {useIsFreeTariff, IUserOutput, userSelector} from 'entities/user';

import {tagsAPI} from '../api/tagsAPI';
import useBatchEditFields from './useBatchEditFields';
import useBatchOperation from './useBatchOperation';
import useBatchResult from './useBatchResult';
import IBatchResult from './IBatchResult';
import {
    getBatchResultCountLabel,
    getBatchResultTitle,
    getBatchSubmitText,
    IBatchOperationRequest,
    IBuildBatchRequestParams,
    isBatchSubmitDisabled,
} from '../lib/batchOperation';

export interface IUseMonitorBatchOperationsFormParams<T extends FieldValues> {
    actionValue?: string,
    handleClose: () => void,
    onSuccess?: () => void,
    selectedIds: string[],
    getDefaultValues: (_user: IUserOutput | null, _freeTariff: boolean) => DefaultValues<T>,
    getUrl: (_actionValue?: string) => string,
    buildRequest: (_params: IBuildBatchRequestParams<T>) => IBatchOperationRequest | undefined,
    invalidate: (_actionValue: string | undefined, _selectedIds: string[]) => void,
    canSubmitEdit?: (_addedFields: string[], _form: UseFormReturn<T>) => boolean,
    validateEdit?: (_addedFields: string[]) => string | undefined,
    onFieldRemoved?: (_key: string, _form: UseFormReturn<T>) => void,
    getResultTitle?: (_actionValue?: string) => string,
}

export interface IMonitorBatchOperationsFormState<T extends FieldValues> {
    form: UseFormReturn<T>,
    user: IUserOutput | null,
    freeTariff: boolean,
    result: IBatchResult | undefined,
    isLoading: boolean,
    progress: number,
    actionValue?: string,
    isEdit: boolean,
    isTags: boolean,
    selectedCount: number,
    addedFields: string[],
    addField: (_key: string) => void,
    removeField: (_key: string) => void,
    editError: string | undefined,
    tagsError: string | undefined,
    isSubmitDisabled: boolean,
    resultTitle: string,
    resultCountLabel: string | undefined,
    submitText: string,
    submit: ReturnType<UseFormReturn<T>['handleSubmit']>,
    handleCloseResult: () => void,
    handleCancel: () => void,
}

export const useMonitorBatchOperationsForm = <T extends FieldValues>({
    actionValue,
    handleClose,
    onSuccess,
    selectedIds,
    getDefaultValues,
    getUrl,
    buildRequest,
    invalidate,
    canSubmitEdit = (addedFields) => addedFields.length > 0,
    validateEdit,
    onFieldRemoved,
    getResultTitle = getBatchResultTitle,
}: IUseMonitorBatchOperationsFormParams<T>): IMonitorBatchOperationsFormState<T> => {
    const {result, showResult, resetResult} = useBatchResult();
    const user = useSelector(userSelector);
    const freeTariff = useIsFreeTariff();
    const {trigger, isLoading, progress} = useBatchOperation();
    const {refetch: refetchTags} = tagsAPI.useGetTagsQuery();

    const isEdit = actionValue === 'edit';
    const isTags = actionValue === 'tags';

    const form = useForm<T>({
        mode: 'onChange',
        defaultValues: getDefaultValues(user, freeTariff),
    });

    const {addedFields, addField, removeField} = useBatchEditFields(
        onFieldRemoved ? (key) => onFieldRemoved(key, form) : undefined,
    );

    const editError = isEdit ? validateEdit?.(addedFields) : undefined;

    const handleBatchSuccess = (batchResult: IBatchResult) => {
        onSuccess?.();
        invalidate(actionValue, selectedIds);
        refetchTags();
        showResult(batchResult);
    };

    const onSubmit: SubmitHandler<T> = async(formData) => {
        const request = buildRequest({actionValue, formData, addedFields, selectedIds});

        if (!request) {
            return;
        }

        try {
            await trigger(getUrl(actionValue), request.method, request.data, handleBatchSuccess);
        } catch {
        }
    };

    const handleCloseResult = () => {
        handleClose();
        resetResult();
    };

    return {
        form,
        user,
        freeTariff,
        result,
        isLoading,
        progress,
        actionValue,
        isEdit,
        isTags,
        selectedCount: selectedIds.length,
        addedFields,
        addField,
        removeField,
        editError,
        tagsError: form.formState.errors.tags?.message as string | undefined,
        isSubmitDisabled: isBatchSubmitDisabled({
            actionValue,
            tagsValue: form.watch('tags' as Path<T>),
            canSubmitEdit: canSubmitEdit(addedFields, form) && !editError,
            isValid: form.formState.isValid,
        }),
        resultTitle: getResultTitle(actionValue),
        resultCountLabel: getBatchResultCountLabel(actionValue),
        submitText: getBatchSubmitText(actionValue),
        submit: form.handleSubmit(onSubmit),
        handleCloseResult,
        handleCancel: handleClose,
    };
};

export default useMonitorBatchOperationsForm;
